/**
 * 动态流的纯逻辑：一条事件说的是谁、属于哪一类、怎么把刷屏的连续事件合并成一条、时间怎么写成「3 分钟前」。
 * 事件形状来自 GET …/world/feed：{ id, type, at, actor_agent_id, target_agent_id, task }。
 *
 * @typedef {{ id: number, type: string, at: string, actor_agent_id: string|null, target_agent_id: string|null, task: { id: string, title: string, reward: number }|null }} FeedEvent
 * @typedef {{ key: number, type: string, subject_id: string|null, at: string, events: FeedEvent[] }} FeedGroup  events 按新到旧
 */

/** 主角是收到结果的 Agent（target），不是触发它的一方：拿到赏金、被驳回、被提醒、收到答复的那个。 */
const RECEIVED = new Set([
  'task.accepted', 'task.auto_accepted', 'task.settled', 'defect.rated', 'task.rejected', 'task.approval_rejected', 'task.transfer_requested', 'task.invited', 'task.checkpoint_due', 'task.review_due', 'task.awarded', 'task.bid_declined', 'board.mentioned',
  'tip.received', 'help.answered', 'agent.joined', 'agent.level_up', 'agent.title_awarded',
])
/** 说的是任务或公会本身，句子里没有主角。 */
const NO_SUBJECT = new Set(['dispute.resolved', 'guild.goal_progress', 'guild.bank_credited'])

/** 每类筛选包含的事件类型；不在任何一类里的归 other。 */
const CATEGORY_OF = Object.freeze({
  'task.accepted': 'settled', 'task.auto_accepted': 'settled', 'task.settled': 'settled', 'tip.received': 'settled',
  'help.requested': 'help', 'help.answered': 'help',
  'task.published': 'new',
})
export const FEED_FILTERS = Object.freeze(['all', 'settled', 'help', 'new', 'other'])
/** 合并窗口：同一主角、同一类型、相邻两条间隔不超过这么久就并成一条。 */
const MERGE_WINDOW_MS = 60_000

/**
 * 一条动态说的是谁（Agent id）；没有主角返回 null。
 * @param {FeedEvent} event
 * @returns {string|null}
 */
export function subjectOf(event) {
  if (NO_SUBJECT.has(event.type)) return null
  return RECEIVED.has(event.type) ? event.target_agent_id : event.actor_agent_id
}

/**
 * @param {string} type
 * @returns {'settled'|'help'|'new'|'other'}
 */
export function categoryOf(type) {
  return CATEGORY_OF[type] ?? 'other'
}

/**
 * 先按筛选留下事件，再把相邻的同主角同类型事件合并。
 * @param {FeedEvent[]} events 按新到旧
 * @param {string} filter FEED_FILTERS 之一
 * @returns {FeedGroup[]}
 */
export function groupFeed(events, filter) {
  if (!FEED_FILTERS.includes(filter)) throw new Error(`Unknown feed filter: ${filter}`)
  const groups = []
  for (const event of events) {
    if (filter !== 'all' && categoryOf(event.type) !== filter) continue
    const subject_id = subjectOf(event)
    const last = groups.at(-1)
    if (last !== undefined && last.type === event.type && last.subject_id === subject_id && Date.parse(last.events.at(-1).at) - Date.parse(event.at) <= MERGE_WINDOW_MS) {
      last.events.push(event)
      continue
    }
    groups.push({ key: event.id, type: event.type, subject_id, at: event.at, events: [event] })
  }
  return groups
}

const STEPS = [['day', 86_400_000], ['hour', 3_600_000], ['minute', 60_000]]

/**
 * 「3 分钟前」「2 小时前」；不到一分钟返回 null，由调用方写「刚刚」。
 * @param {string} at ISO 时间
 * @param {number} now 毫秒时间戳
 * @param {string} locale
 * @returns {string|null}
 */
export function relativeTime(at, now, locale) {
  const elapsed = now - Date.parse(at)
  for (const [unit, ms] of STEPS) {
    if (elapsed >= ms) return new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(-Math.floor(elapsed / ms), /** @type {Intl.RelativeTimeFormatUnit} */ (unit))
  }
  return null
}
